import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers, MessageSquare, CalendarDays, X } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { SHORE } from "@/lib/images";
import { Button } from "@/components/ui/button";

const CAN = [
  [Layers, "Explore the board", "Every room, swatch and material, laid out as it's planned."],
  [MessageSquare, "Leave comments", "Click any item on the board to share a thought or a question."],
  [CalendarDays, "Follow the schedule", "See what's happening on site and what's coming next."],
];

export default function ClientWelcome({ project }) {
  const { user, brand } = useAuth();
  const [open, setOpen] = useState(!user.welcomed_at);
  const photos = useFetch(project ? `/projects/${project.id}/photos` : null);
  if (!open) return null;
  const photo = photos.data?.[0];
  const dismiss = () => { setOpen(false); api.post("/auth/welcome-seen").catch(() => {}); };
  return (
    <div className="rise relative mb-10 grid overflow-hidden rounded-2xl border bg-card lg:grid-cols-[1.1fr_1fr]" data-testid="client-welcome-card">
      <div className="relative min-h-56">
        <img src={photo ? fileUrl(photo.url) : SHORE} alt={project?.name || "Your project"} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        {project && <p className="absolute bottom-5 left-6 font-serif text-2xl text-white" data-testid="client-welcome-project">{project.name}</p>}
      </div>
      <div className="space-y-6 p-8">
        <button type="button" onClick={dismiss} aria-label="Dismiss welcome" className="absolute right-4 top-4 rounded-full p-1 text-muted-foreground hover:bg-secondary" data-testid="client-welcome-dismiss"><X className="h-4 w-4" /></button>
        <div>
          <p className="eyebrow eyebrow-accent eyebrow-accent">Welcome</p>
          <h2 className="mt-2 font-serif text-3xl leading-tight sm:text-4xl" data-testid="client-welcome-greeting">{(user.name || "").split(" ")[0] || "Hello"}, welcome to {brand.name}.</h2>
          <p className="mt-3 text-sm text-muted-foreground">{brand.branding?.tagline ? `${brand.branding.tagline}. ` : ""}This is your window into the project: everything we're planning, as it takes shape.</p>
        </div>
        <ul className="space-y-3">
          {CAN.map(([Icon, t, d]) => <li key={t} className="flex gap-3 text-sm"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" /><span><strong className="font-medium">{t}.</strong> <span className="text-muted-foreground">{d}</span></span></li>)}
        </ul>
        <div className="flex flex-wrap gap-2">
          {project && <Button asChild className="rounded-full" onClick={dismiss} data-testid="client-welcome-open-project"><Link to={`/projects/${project.id}`}>Open my project <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>}
          <Button variant="ghost" className="rounded-full" onClick={dismiss} data-testid="client-welcome-got-it">Got it</Button>
        </div>
      </div>
    </div>
  );
}
